import React, { useCallback, useState } from 'react'

import cx from 'classnames'
import { CallHistoryMethodAction } from 'connected-react-router'
import Badge from 'conversations/components/Badge'
import { ConversationsListMenu } from 'conversations/components/NewChatSidebar/ConversationsListMenu'
import useBadgeCounts from 'conversations/hooks/useBadgeCounts'
import useSectionNotifications, {
  SectionNotification,
} from 'conversations/hooks/useSectionNotifications'
import { useSectionConfig, useSectionConfigProperty } from 'conversations/hooks/useSections'
import { isDefined } from 'conversations/utils/typescriptHelpers'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { deleteConvoSidebarSectionAsync } from 'shared/store/actions/user/sections'
import { useDebounce } from 'use-debounce'

import { Icon, IconButton, Tooltip } from '@driftt/dds'
import { faBell } from '@driftt/dds.icons/regular/faBell'
import { faBellSlash } from '@driftt/dds.icons/regular/faBellSlash'
import { faAngleRight } from '@driftt/dds.icons/solid/faAngleRight'
import { faEllipsis } from '@driftt/dds.icons/solid/faEllipsis'

import { RootAction } from 'typesafe-actions'

import styles from './styles.module.css'

interface Props {
  expanded: boolean
  name: string
  sectionID?: string
  'data-ocweb-id': string
  tooltipContent?: string
  inboxId: number
  onSectionLengthChange: (length: number) => void
}

const HeaderWithNotificationsBell: React.FC<{
  sectionID?: string
  name: string
}> = ({ sectionID, name }): JSX.Element => {
  const subscriptionId = useSectionConfigProperty(sectionID, 'subscriptionId')
  const sectionNotification = useSectionNotifications(subscriptionId)
  const notificationsEnabled = sectionNotification === SectionNotification.ON
  return (
    <div className={styles.conversationListHeaderContainer}>
      <div className={styles.conversationListHeaderText}>{name}</div>
      {sectionNotification !== SectionNotification.NONE && (
        <div data-ocweb-id="SectionNotificationIcon">
          <Tooltip
            side="bottom"
            content={<div>Notifications are {notificationsEnabled ? 'on' : 'off'}</div>}>
            <Icon
              size={notificationsEnabled ? 'medium' : 'large'}
              icon={notificationsEnabled ? faBell : faBellSlash}
              variant="gray"
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export interface BadgeCountsSelector {
  total: number
  [key: number]: Record<number, number>
}

const ConversationListHeader: React.FC<Props> = ({
  expanded,
  name,
  'data-ocweb-id': dataTestId,
  sectionID,
  inboxId,
  onSectionLengthChange,
}): JSX.Element => {
  const [menuActive, setMenuActive] = useState(false)
  const sectionConfig = useSectionConfig(sectionID)
  const badgeCounts: BadgeCountsSelector = useBadgeCounts()
  const { total: badgeTotal } = badgeCounts
  const dispatch = useDispatch<Dispatch<RootAction | CallHistoryMethodAction>>()

  const myConvoBadgeCountForInbox = badgeCounts[inboxId]
    ? Object.keys(badgeCounts[inboxId]).length
    : 0

  // undefined means "all inboxes"
  const countToDisplay = inboxId ? myConvoBadgeCountForInbox : badgeTotal
  const [debouncedCount] = useDebounce(countToDisplay, 750)
  const showCount = sectionConfig?.type === 'my-convos' && countToDisplay > 0 && !expanded

  const deleteSection = useCallback(() => {
    if (sectionConfig && isDefined(sectionConfig.id)) {
      dispatch(
        deleteConvoSidebarSectionAsync.request({
          title: sectionConfig.title,
          id: sectionConfig.id,
          shared: sectionConfig.shared,
        })
      )
    }
  }, [dispatch, sectionConfig])

  return (
    <div
      className={styles.conversationListHeader}
      data-ocweb-id={`${dataTestId}${expanded ? 'Expanded' : 'Collapsed'}`}>
      <HeaderWithNotificationsBell sectionID={sectionID} name={name} />
      <ConversationsListMenu
        sectionConfig={sectionConfig}
        active={menuActive}
        deleteSection={deleteSection}
        setActive={setMenuActive}
        onSectionLengthChange={onSectionLengthChange}>
        {showCount ? (
          <Badge count={debouncedCount} />
        ) : (
          <IconButton
            title={`${sectionConfig?.shared ? 'Shared section' : 'Section'} options`}
            icon={faEllipsis}
            variant="tertiary"
            iconVariant="gray"
            size="small"
          />
        )}
      </ConversationsListMenu>
      <IconButton
        title=""
        className={cx(styles.expandedIcon, {
          [styles.collapsed]: !expanded,
        })}
        size="small"
        icon={faAngleRight}
        variant="tertiary"
        iconVariant="gray"
      />
    </div>
  )
}

export default ConversationListHeader
